import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, Check, Paperclip, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, getSiteConfig, waLink } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const OCCASIONS = ["Corporate Gifting", "Festive Gifting", "Welcome / Onboarding Kit", "Wedding", "Personal Celebration", "Other"];

export default function Enquire() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "",
    occasion: "", quantity: "", message: "", product_name: "",
  });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [config, setConfig] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    getSiteConfig().then(setConfig);
    const occasion = searchParams.get("occasion");
    if (occasion) setForm((f) => ({ ...f, occasion }));
    const msg = searchParams.get("message");
    if (msg) setForm((f) => ({ ...f, message: msg }));
    const pid = searchParams.get("product");
    if (pid) {
      api.get(`/products/${pid}`).then((r) => {
        setForm((f) => ({
          ...f,
          product_name: r.data.name,
          message: f.message || `I'm interested in "${r.data.name}". `,
        }));
      }).catch(() => {});
    }
  }, [searchParams]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("file", file);
      await api.post("/enquiries", fd);
      setDone(true);
      toast.success("Enquiry received. We'll respond within one business day.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or reach us on WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <main className="pt-40 pb-32 px-6 min-h-screen">
        <div className="max-w-xl mx-auto text-center">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-gold/40 bg-white">
            <Check size={26} className="text-gold-brass" />
          </span>
          <h1 className="mt-8 font-serif font-light text-4xl sm:text-5xl text-ink">Enquiry received.</h1>
          <p className="mt-5 text-base text-ink-muted leading-relaxed">
            Thank you, {form.name.split(" ")[0] || "friend"}. Your brief is with our studio —
            expect a considered response within one business day.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            {config && (
              <a
                data-testid="enquiry-whatsapp-continue"
                href={waLink(config.whatsapp_number, `Hello Gratiora! I just submitted an enquiry${form.product_name ? ` about "${form.product_name}"` : ""}.`)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] text-white px-8 py-4 text-sm font-medium tracking-wide hover:brightness-95 transition"
              >
                <MessageCircle size={16} /> Continue on WhatsApp
              </a>
            )}
            <Link to="/catalogue" className="text-sm text-gold-brass gold-underline-btn font-medium">
              Keep browsing
            </Link>
          </div>
        </div>
      </main>
    );
  }

  const inputCls =
    "w-full bg-white border border-gold/20 px-4 py-3.5 text-sm text-ink placeholder:text-ink/40 focus:outline-none focus:border-gold transition-colors duration-300";

  return (
    <main className="pt-32 pb-24 px-6 lg:px-12 max-w-7xl mx-auto min-h-screen">
      <div className="grid lg:grid-cols-12 gap-14">
        <Reveal className="lg:col-span-5">
          <p className="eyebrow">Request a Quote</p>
          <h1 className="mt-4 font-serif font-light text-4xl sm:text-5xl text-ink tracking-tight text-balance">
            Tell us what you're celebrating.
          </h1>
          <p className="mt-6 text-base text-ink-muted leading-relaxed max-w-md">
            Share the occasion, the audience and roughly how many you're gifting.
            Attach your logo or a brief if you have one — we'll come back with
            curated directions, samples and a composed quote.
          </p>
          <div className="mt-10 space-y-4 text-sm text-ink/70">
            <p><span className="text-gold-brass font-medium">Response time</span> — within one business day</p>
            <p><span className="text-gold-brass font-medium">Typical MOQ</span> — 25 units (100 for favours)</p>
            <p><span className="text-gold-brass font-medium">Delivery</span> — pan-India, to one or many addresses</p>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-7">
          <form data-testid="enquiry-form" onSubmit={submit} className="bg-white border border-gold/15 p-8 lg:p-10 space-y-6">
            {form.product_name && (
              <p className="font-mono text-xs uppercase tracking-[0.15em] text-gold-brass border border-gold/25 bg-cream px-4 py-3">
                Regarding · {form.product_name}
              </p>
            )}
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="eyebrow !text-[10px] block mb-2">Name *</label>
                <input data-testid="enquiry-input-name" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} />
              </div>
              <div>
                <label className="eyebrow !text-[10px] block mb-2">Company</label>
                <input data-testid="enquiry-input-company" value={form.company} onChange={set("company")} placeholder="Company or family name" className={inputCls} />
              </div>
              <div>
                <label className="eyebrow !text-[10px] block mb-2">Email *</label>
                <input data-testid="enquiry-input-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@company.com" className={inputCls} />
              </div>
              <div>
                <label className="eyebrow !text-[10px] block mb-2">Phone *</label>
                <input data-testid="enquiry-input-phone" type="tel" required value={form.phone} onChange={set("phone")} placeholder="+91 ..." className={inputCls} />
              </div>
              <div>
                <label className="eyebrow !text-[10px] block mb-2">Occasion</label>
                <select data-testid="enquiry-select-occasion" value={form.occasion} onChange={set("occasion")} className={inputCls}>
                  <option value="">Select an occasion</option>
                  {OCCASIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="eyebrow !text-[10px] block mb-2">Quantity / MOQ</label>
                <input data-testid="enquiry-input-quantity" value={form.quantity} onChange={set("quantity")} placeholder="e.g. 50–100 units" className={inputCls} />
              </div>
            </div>
            <div>
              <label className="eyebrow !text-[10px] block mb-2">Your brief</label>
              <textarea data-testid="enquiry-input-message" rows={4} value={form.message} onChange={set("message")} placeholder="Tell us about the audience, timeline, budget range, branding needs..." className={`${inputCls} resize-none`} />
            </div>
            <div>
              <label className="eyebrow !text-[10px] block mb-2">Logo / brief file</label>
              <button
                type="button"
                data-testid="enquiry-file-upload"
                onClick={() => fileRef.current?.click()}
                className="w-full border border-dashed border-gold/40 bg-cream/60 px-4 py-8 text-sm text-ink/60 hover:border-gold hover:bg-cream transition-colors duration-300 flex flex-col items-center gap-2"
              >
                <Paperclip size={18} className="text-gold-brass" />
                {file ? <span className="text-ink font-medium">{file.name}</span> : "Click to attach a logo or brief (image or PDF)"}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*,.pdf"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </div>
            <button
              data-testid="enquiry-submit-button"
              type="submit"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 bg-ink text-cream px-8 py-4.5 py-4 text-sm tracking-wide hover:bg-gold-brass transition-colors duration-400 disabled:opacity-60 group"
            >
              {submitting ? "Sending..." : "Submit Enquiry"}
              {!submitting && <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />}
            </button>
          </form>
        </Reveal>
      </div>
    </main>
  );
}
